import { useState, useEffect, useCallback } from "react";
import {
  EntityTypeEnum,
  RegtankOnboardingFHStatusEnum,
  RegtankLivenessFHStatusEnum,
} from "@funded-here-interface/common/src/constant/enum";
import {
  changeRegtankStatusAttemptUsed,
  changeRegtankLivenessData,
  resultOnboardingChecking,
  resultLivenessChecking,
} from "../../../../Utils/getRegtankDetails";
import type {
  RegtankData,
  RegtankLivenessData,
  RegtankHistoryData,
  RegtankLivenessHistoryData,
} from "../Regtank.types";
import type { Data, UserData } from "../../../../types/org.type";
import { convertToUtcTwelveHours } from "@funded-here-interface/common/src/Utils/formatter";
import { sortRegtankRequestsByExpiry } from "../../../../Utils/regtankUtils";

interface UseRegtankDataProps {
  userData: UserData[] | undefined;
  orgData: Data | undefined;
  updatedFinalData: RegtankData[];
  updatedLivenessFinalData: RegtankLivenessData[];
}

/**
 * Calculates the updated attempt date based on sorted data and result
 */
const calculateAttemptDate = (
  sortedData: any[],
  newSubmittedOn: string
): string => {
  if (sortedData.length > 1) {
    const lastSubmittedDate = sortedData[sortedData.length - 1].submittedDate;
    return newSubmittedOn === "" && lastSubmittedDate
      ? convertToUtcTwelveHours(lastSubmittedDate)
      : convertToUtcTwelveHours(newSubmittedOn);
  }
  return newSubmittedOn !== "" ? convertToUtcTwelveHours(newSubmittedOn) : "";
};

/**
 * Custom hook to handle all data transformation and processing
 * Consolidates the massive useEffect logic into a single, manageable hook
 */
export const useRegtankData = ({
  userData,
  orgData,
  updatedFinalData,
  updatedLivenessFinalData,
}: UseRegtankDataProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [finalData, setFinalData] = useState<RegtankData[]>([]);
  const [livenessFinalData, setLivenessFinalData] = useState<
    RegtankLivenessData[]
  >([]);
  const [regtankFHResultTotal, setRegtankFHResultTotal] = useState<
    resultOnboardingChecking[]
  >([]);
  const [livenessFHResultTotal, setLivenessFHResultTotal] = useState<
    resultLivenessChecking[]
  >([]);
  const [verificationHistoryData, setVerificationHistoryData] = useState<
    RegtankHistoryData[]
  >([]);
  const [livenessHistoryData, setLivenessHistoryData] = useState<
    RegtankLivenessHistoryData[]
  >([]);
  const [regtankOnboardingPerUserRowData, setRegtankOnboardingPerUserRowData] =
    useState<RegtankData>();
  const [regtankLivenessCardData, setRegtankLivenessCardData] =
    useState<RegtankLivenessData>();

  const getUserType = useCallback(
    (data: UserData): string => {
      let userType = "";
      if (
        data?.orgRole?.[0].role?.name === "investor" &&
        data?.entityType === EntityTypeEnum.INDIVIDUAL
      ) {
        userType = "Individual Investor";
      } else {
        const isDirector = orgData?.boardOfDirectors.find(
          (x) => x.email === data.email
        );
        const isSuspended = data.isSuspended;
        const isAuthRep =
          orgData?.emailOfAuthorisedRepresentiative === data.email;
        if (isDirector && isAuthRep) {
          userType = "Director & Auth Rep";
        } else if (isDirector) {
          userType = "Director";
        } else if (isAuthRep) {
          userType = "Auth Rep";
        } else if (isSuspended) {
          userType = "Removed User";
        }
      }
      return userType;
    },
    [orgData]
  );

  /**
   * Generic data processor for both onboarding and liveness data
   */
  const processRegtankData = useCallback(
    <TData, TResult>(config: {
      rawDataKey: "regtankRequest" | "regtankLiveness";
      processResult: (sortedData: any[]) => TResult;
      buildTableData: (
        user: UserData,
        result: TResult,
        attemptDate: string
      ) => TData;
      buildEmptyTableData: (user: UserData) => TData;
      shouldProcess: (user: UserData) => boolean;
    }): { tableData: TData[]; results: TResult[] } => {
      const tableData: TData[] = [];
      const results: TResult[] = [];

      if (!userData || userData.length === 0) {
        return { tableData, results };
      }

      for (const user of userData) {
        if (user?.regtankOnboarding) {
          const rawData = user[config.rawDataKey];

          if (rawData && rawData.length > 0) {
            const sortedData = sortRegtankRequestsByExpiry(rawData);
            const result = config.processResult(sortedData);
            const attemptDate = calculateAttemptDate(
              sortedData,
              (result as any).newSubmittedOn
            );

            const data = config.buildTableData(user, result, attemptDate);
            results.push(result);
            tableData.push(data);
          } else if (config.shouldProcess(user)) {
            const data = config.buildEmptyTableData(user);
            tableData.push(data);
          }
        }
      }

      return { tableData, results };
    },
    [userData]
  );

  // Process onboarding data
  useEffect(() => {
    if (!userData || userData.length === 0 || !userData[0].regtankRequest) {
      setFinalData([]);
      return;
    }

    const { tableData, results } = processRegtankData<
      RegtankData,
      resultOnboardingChecking
    >({
      rawDataKey: "regtankRequest",
      processResult: (sortedData) =>
        changeRegtankStatusAttemptUsed(
          sortedData[0].requestId,
          sortedData[0].onboardingStatus,
          sortedData[0].expiredDate,
          sortedData[0].reattemptApproval,
          sortedData.length - 1,
          sortedData[0].submittedDate,
          sortedData[0].reviewedDate,
          sortedData[0].remark,
          sortedData[0].reason,
          sortedData[0].documentIdSubmittedDate
        ),
      buildTableData: (user, result, attemptDate) => ({
        name: user.firstName + " " + user.lastName,
        email: user.email,
        userRole: getUserType(user),
        onboardingStatus: result.newStatus,
        lastAttemptDate: attemptDate,
        attemptUsed: result.newAttemptUsed,
        userId: user.id,
      }),
      buildEmptyTableData: (user) => ({
        name: user.firstName + " " + user.lastName,
        email: user.email,
        userRole: getUserType(user),
        onboardingStatus: RegtankOnboardingFHStatusEnum.PENDING_VERIFICATION,
        lastAttemptDate: " ",
        attemptUsed: "",
        userId: user.id,
      }),
      shouldProcess: () => true,
    });

    setRegtankFHResultTotal(results);

    // Apply updates if available
    if (updatedFinalData.length > 0 && finalData.length > 0) {
      const updatedMap = new Map<number, RegtankData>(
        updatedFinalData.map((x) => [x.userId, x])
      );
      setFinalData((prev) =>
        prev.map((item) => {
          const updated = updatedMap.get(item.userId);
          return updated ? { ...item, ...updated } : item;
        })
      );
    } else {
      setFinalData(tableData);
    }
  }, [userData, updatedFinalData, getUserType, processRegtankData]);

  // Process liveness data
  useEffect(() => {
    if (!userData || userData.length === 0) {
      setLivenessFinalData([]);
      setIsLoading(false);
      return;
    }

    const { tableData, results } = processRegtankData<
      RegtankLivenessData,
      resultLivenessChecking
    >({
      rawDataKey: "regtankLiveness",
      processResult: (sortedData) =>
        changeRegtankLivenessData(
          sortedData[0].sessionId,
          sortedData[0].livenessStatus,
          sortedData[0].expiredDate,
          sortedData.length - 1,
          sortedData[0].reattemptApproval,
          sortedData[0].submittedDate,
          sortedData[0].reason
        ),
      buildTableData: (user, result, attemptDate) => ({
        name: user.firstName + " " + user.lastName,
        email: user.email,
        userRole: getUserType(user),
        verificationStatus: result.newStatus,
        lastAttemptDate: attemptDate,
        userId: user.id,
      }),
      buildEmptyTableData: (user) => ({
        name: user.firstName + " " + user.lastName,
        email: user.email,
        userRole: getUserType(user),
        verificationStatus: RegtankLivenessFHStatusEnum.NOTHING,
        lastAttemptDate: " ",
        userId: user.id,
      }),
      shouldProcess: () => true,
    });

    setLivenessFHResultTotal(results);

    // Apply updates if available
    if (updatedLivenessFinalData.length > 0 && livenessFinalData.length > 0) {
      const updatedMap = new Map<number, RegtankLivenessData>(
        updatedLivenessFinalData.map((x) => [x.userId, x])
      );
      setLivenessFinalData((prev) =>
        prev.map((item) => {
          const updated = updatedMap.get(item.userId);
          return updated ? { ...item, ...updated } : item;
        })
      );
    } else {
      setLivenessFinalData(tableData);
    }

    setIsLoading(false);
  }, [userData, updatedLivenessFinalData, getUserType, processRegtankData]);

  return {
    isLoading,
    finalData,
    setFinalData,
    livenessFinalData,
    setLivenessFinalData,
    regtankFHResultTotal,
    livenessFHResultTotal,
    verificationHistoryData,
    setVerificationHistoryData,
    livenessHistoryData,
    setLivenessHistoryData,
    regtankOnboardingPerUserRowData,
    setRegtankOnboardingPerUserRowData,
    regtankLivenessCardData,
    setRegtankLivenessCardData,
    convertToUtcTwelveHours,
    getUserType,
  };
};
